import { useSidebarContext } from '@/app/layout2/Sidebar';
import { UserImpersonation } from '@/app/layout2/UserImpersonation';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { selectLoggedInUser, selectUserIsLoading, useAppSelector, isOrganisationWithBilling } from '@app.raytd.com/store';
import { CircleUser } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';

interface UserMenuProps {
  enableMenu?: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({ enableMenu = true }) => {

  const { showSidebar } = useSidebarContext();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger disabled={!enableMenu}>
        <UserProfile showName={showSidebar} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>My Account</DropdownMenuLabel>
        {process.env.NODE_ENV === 'development' && (
          <DropdownMenuItem><UserImpersonation /></DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem><Link to="/profile">Profile</Link></DropdownMenuItem>
        <DropdownMenuItem><Link to="/organisation">Organisation</Link></DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem><Link to="/logout">Logout</Link></DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};


interface UserProfileProps {
  className?: string;
  showName?: boolean;
}

export const UserProfile = React.forwardRef<HTMLDivElement, UserProfileProps>(
  ({ className, showName }, ref) => {
    const userIsLoading = useAppSelector(selectUserIsLoading);
    const user = useAppSelector(selectLoggedInUser);

    if (userIsLoading) {
      return <CircleUser className="w-6 h-6 animate-spin" />
    }

    // Check if user has Pro access (active subscription OR on trial)
    // Note: Don't check license_type as it can be stale. Check billing status instead.
    const hasProLicense = (() => {
      if (!user?.company) return false
      const company = user.company
      if (!isOrganisationWithBilling(company)) return false
      // TypeScript now knows company is OrganisationWithBilling
      const { billing } = company
      
      // Show Pro badge if:
      // 1. Organisation has active subscription (even if trial period remains)
      // 2. Organisation is on trial (trial users get Pro access)
      // Don't check license_type as it may be stale - billing status is the source of truth
      return billing.is_subscribed || 
             billing.stripe_status === 'active' ||
             billing.on_trial ||
             billing.stripe_status === 'trialing'
    })()

    return (
      <div
        ref={ref}
        className={cn("flex items-center gap-2 px-0 cursor-pointer overflow-hidden bg-transparent border-none", className)}
      >
        <div className="relative flex-shrink-0">
          <Avatar className="flex-shrink-0">
            <AvatarImage
              src={user?.avatar}
              alt={user?.name}
              className='object-cover'
              onError={(e) => {
                console.error('Avatar image failed to load:', {
                  src: user?.avatar,
                  error: e
                })
              }}
            />
            <AvatarFallback>{user?.name[0]}</AvatarFallback>
          </Avatar>
          {hasProLicense && (
            <Badge
              className="absolute -bottom-0.5 -right-1 h-3.5 px-1.5 text-[9px] bg-black text-white border-0"
            >
              Pro
            </Badge>
          )}
        </div>
        <span className="sr-only">Open user profile menu</span>
        <span className={cn(
          "text-sm font-semibold text-zinc-600 whitespace-pre-line break-words transition-all duration-200 ease-in-out",
          showName ? "opacity-100 max-w-[120px]" : "opacity-0 max-w-0"
        )}>
          {user?.name}
        </span>
      </div>
    )
  }
)

UserProfile.displayName = 'UserProfile'

export default UserMenu;
